import { useEffect, useState } from "react";
import { devWarn } from "@/lib/log";
import { api, formatApiError } from "@/lib/api";
import { fmtNumber, fmtMoney } from "@/lib/fmt";
import { Plus, Pencil, Trash2, X, KeyRound, RotateCcw, Mail, Send, Search } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

const DEFAULT_PWD = "Welcome2026";

export default function AdminAuthors() {
  const [authors, setAuthors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = () => api.get("/admin/authors").then(({ data }) => setAuthors(data));
  useEffect(() => { load(); }, []);

  const onAudit = async (a) => {
    setAudit({ author: a, data: null });
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/admin/authors/${a.id}/sales-audit`);
      setAudit({ author: a, data });
    } catch (e) {
      toast.error(formatApiError(e));
      setAudit(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const onSave = async (form) => {
    setErr("");
    try {
      if (editing) {
        await api.patch(`/admin/authors/${editing.id}`, form);
      } else {
        await api.post("/admin/authors", form);
      }
      setEditing(null); setShowNew(false);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this author? Their login and titles will be removed.")) return;
    await api.delete(`/admin/authors/${id}`);
    load();
  };

  const onGenerateResetLink = async (a) => {
    try {
      const { data } = await api.post(`/admin/authors/${a.id}/reset-link`);
      const ok = await copyToClipboard(data.reset_url);
      if (ok) toast.success(`Reset link copied · valid until ${new Date(data.expires_at).toLocaleString()}`);
      else window.prompt("Copy this reset link and send it to the author:", data.reset_url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onResetAllToDefault = async () => {
    const ok = window.confirm(
      "Reset ALL author passwords to the default (Welcome2026)?\n\n" +
      "This is for onboarding day. Authors will be forced to choose a new password on first login. " +
      "Authors who've already set a personal password will lose it."
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/authors/reset-passwords-to-default");
      toast.success(`Reset ${data.reset_count} authors to "${data.default_password}"`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onResetSinglePwd = async (a) => {
    if (!window.confirm(`Reset ${a.name}'s password to Welcome2026?`)) return;
    try {
      const { data } = await api.post(`/admin/authors/${a.id}/reset-password`);
      toast.success(`${a.name}'s password reset to "${data.default_password}"`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSendWelcomeEmails = async () => {
    const ok = window.confirm(
      "Send the welcome email (with login URL + temp password) to ALL authors?\n\n" +
      "This will:\n" +
      "  • Reset every author's password to Welcome2026\n" +
      "  • Email each author their login details via Resend\n\n" +
      "Continue?"
    );
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/authors/send-welcome-emails");
      const msg = `Sent ${data.sent_count} welcome emails` + (data.failed_count ? ` · ${data.failed_count} failed` : "");
      if (data.failed_count) {
        toast.warning(msg + ". Check console for details.");
        devWarn("Failed welcome emails:", data.failed);
      } else {
        toast.success(msg);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSendSingleWelcomeEmail = async (a) => {
    if (!window.confirm(`Send welcome email to ${a.name} (${a.email})?\n\nThis resets the password to Welcome2026 and emails it to them.`)) return;
    try {
      const { data } = await api.post("/admin/authors/send-welcome-emails", { author_ids: [a.id] });
      if (data.sent_count > 0) toast.success(`Welcome email sent to ${a.email}`);
      else toast.error(`Send failed: ${data.failed?.[0]?.error || "unknown"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-authors-page">
      <header className="flex items-center justify-between">
        <div>
          <p className="hh-overline mb-3">Admin · Authors</p>
          <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Author roster</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onSendWelcomeEmails} className="hh-btn-outline" data-testid="send-all-welcome-emails-btn">
            <Send size={14} strokeWidth={1.8} className="inline mr-2" /> Send welcome emails (all)
          </button>
          <button onClick={onResetAllToDefault} className="hh-btn-outline" data-testid="reset-all-passwords-btn">
            Reset all passwords to default
          </button>
          <button onClick={() => { setShowNew(true); setEditing(null); }} className="hh-btn-primary" data-testid="new-author-btn">
            <Plus size={16} strokeWidth={1.8} className="mr-2" /> New author
          </button>
        </div>
      </header>

      <div className="hh-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-hh-muted/60 border-b border-hh-border">
            <tr className="text-left">
              <th className="px-6 py-4 hh-overline">Author</th>
              <th className="px-6 py-4 hh-overline">Email (login)</th>
              <th className="px-6 py-4 hh-overline">Royalty %</th>
              <th className="px-6 py-4 hh-overline">Titles</th>
              <th className="px-6 py-4 hh-overline">Lifetime units</th>
              <th className="px-6 py-4 hh-overline">Net pub comp</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {authors.map((a) => (
              <tr key={a.id} className="border-b border-hh-border last:border-0" data-testid={`author-row-${a.slug}`}>
                <td className="px-6 py-4 font-serif text-hh-ink">{a.name}</td>
                <td className="px-6 py-4 text-hh-ink2 font-mono text-xs">{a.email}</td>
                <td className="px-6 py-4 text-hh-ink">{a.royalty_pct}%</td>
                <td className="px-6 py-4 text-hh-ink">{fmtNumber(a.title_count)}</td>
                <td className="px-6 py-4 text-hh-ink">{fmtNumber(a.lifetime_units)}</td>
                <td className="px-6 py-4 text-hh-ink2">{fmtMoney(a.lifetime_net_pub_comp, "AUD")}<span className="text-[10px] ml-1">(mixed ccy)</span></td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button onClick={() => onAudit(a)} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2" title="Sales audit — see every transaction attributed to this author + orphan WC orders" data-testid={`audit-${a.slug}`}>
                    <Search size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onSendSingleWelcomeEmail(a)} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2" title="Send welcome email with login details (resets password to Welcome2026)" data-testid={`send-welcome-${a.slug}`}>
                    <Mail size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onGenerateResetLink(a)} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2" title="Copy a one-time password reset link" data-testid={`reset-link-${a.slug}`}>
                    <KeyRound size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onResetSinglePwd(a)} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2" title="Force-reset to Welcome2026 (default)" data-testid={`reset-default-${a.slug}`}>
                    <RotateCcw size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => { setEditing(a); setShowNew(false); }} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2 ml-1" data-testid={`edit-author-${a.slug}`}>
                    <Pencil size={14} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => onDelete(a.id)} className="p-2 rounded-full hover:bg-hh-muted text-hh-danger ml-1" data-testid={`delete-author-${a.slug}`}>
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {authors.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-hh-ink2">No authors yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editing || showNew) && (
        <AuthorForm
          initial={editing}
          onCancel={() => { setEditing(null); setShowNew(false); setErr(""); }}
          onSave={onSave}
          err={err}
        />
      )}

      {audit && (
        <SalesAuditModal
          author={audit.author}
          data={audit.data}
          loading={auditLoading}
          onClose={() => setAudit(null)}
        />
      )}
    </div>
  );
}

function SalesAuditModal({ author, data, loading, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-hh-border max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="sales-audit-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="hh-overline mb-2">Sales audit</p>
            <h3 className="font-serif text-2xl text-hh-ink">{author.name}</h3>
            <p className="text-xs text-hh-ink2 mt-1 font-mono">{author.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-hh-muted text-hh-ink2"><X size={16} /></button>
        </div>

        {loading || !data ? (
          <p className="text-hh-ink2">Loading…</p>
        ) : (
          <div className="space-y-6 text-sm">
            {/* Titles */}
            <section>
              <p className="hh-overline mb-2">Titles registered ({data.titles.length})</p>
              {data.titles.length === 0 ? (
                <p className="text-hh-ink2 italic">No titles registered for this author yet. HH website sales for this author&rsquo;s books <strong>cannot be attributed</strong> until at least one title with the correct ISBN is added on Admin → Titles.</p>
              ) : (
                <ul className="space-y-1">
                  {data.titles.map((t) => (
                    <li key={t.isbn} className="text-hh-ink"><span className="font-mono text-xs text-hh-ink2 mr-2">{t.isbn}</span>{t.title} <span className="text-xs text-hh-ink2">· {t.format}</span></li>
                  ))}
                </ul>
              )}
            </section>

            {/* By source */}
            <section>
              <p className="hh-overline mb-2">Sales by source</p>
              {Object.keys(data.by_source || {}).length === 0 ? (
                <p className="text-hh-ink2 italic">No transactions attributed to this author yet.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(data.by_source).map(([source, s]) => (
                    <div key={source} className="rounded-lg border border-hh-border p-4 bg-hh-cream/30">
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="font-medium text-hh-ink">{source}</span>
                        <span className="text-hh-ink2 text-xs">{s.units} units</span>
                      </div>
                      <div className="text-xs text-hh-ink2 mb-1">Markets: {Object.entries(s.by_market).map(([m, u]) => `${m} (${u})`).join(", ")}</div>
                      <div className="text-xs text-hh-ink2">Net comp: {Object.entries(s.by_currency).map(([c, v]) => `${c} ${v.toFixed(2)}`).join(" · ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* WC orders */}
            <section>
              <p className="hh-overline mb-2">Healing House website orders ({data.wc_orders.length})</p>
              {data.wc_orders.length === 0 ? (
                <p className="text-hh-ink2 italic">No HH-website (WooCommerce) direct orders attributed to this author yet.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-hh-border">
                  <table className="w-full text-xs">
                    <thead className="bg-hh-cream/40 text-hh-ink2">
                      <tr>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Order</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-right px-3 py-2">Units</th>
                        <th className="text-right px-3 py-2">Total AUD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.wc_orders.map((o) => (
                        <tr key={`${o.wc_order_id}-${o.isbn}-${o.date}`} className="border-t border-hh-border/40">
                          <td className="px-3 py-1.5">{o.date}</td>
                          <td className="px-3 py-1.5 font-mono">{o.wc_order_id || "—"}</td>
                          <td className="px-3 py-1.5">{o.customer || "—"}{o.is_house_order && <span className="ml-1 text-[9px] uppercase text-hh-ink2">(house)</span>}</td>
                          <td className="px-3 py-1.5 text-right">{o.units}</td>
                          <td className="px-3 py-1.5 text-right">{o.amount_aud ? `A$${o.amount_aud.toFixed(2)}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {data.orphan_wc_candidates && data.orphan_wc_candidates.length > 0 ? (
              <section>
                <p className="hh-overline mb-2 text-red-700">⚠ Orphan WC orders ({data.orphan_wc_candidates.length})</p>
                <p className="text-xs text-hh-ink2 mb-2">These HH-website transactions have this author&rsquo;s ISBNs but no author_id set. They need manual re-attribution.</p>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function AuthorForm({ initial, onCancel, onSave, err }) {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [bio, setBio] = useState(initial?.bio || "");
  const [avatar, setAvatar] = useState(initial?.avatar_url || "");
  const [pct, setPct] = useState(initial?.royalty_pct ?? 95);

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-8 shadow-2xl" data-testid="author-form">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl text-hh-ink">{initial ? "Edit author" : "Add author"}</h2>
          <button onClick={onCancel}><X size={18} className="text-hh-ink2" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Full name" value={name} onChange={setName} testid="author-name" />
          <Field label="Email (used for login)" value={email} onChange={setEmail} type="email" testid="author-email" />
          <Field label="Royalty %" value={pct} onChange={(v) => setPct(parseFloat(v) || 0)} type="number" testid="author-pct" />
          <Field label="Avatar URL" value={avatar} onChange={setAvatar} optional testid="author-avatar" />
          <div>
            <label className="block text-sm text-hh-ink2 mb-2">Short bio</label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
              data-testid="author-bio"
              className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40" />
          </div>
          {!initial && (
            <p className="text-xs text-hh-ink2 bg-hh-muted px-3 py-2 rounded-lg">
              A login will be created for this email with the default password <span className="font-mono font-medium">{DEFAULT_PWD}</span>.
            </p>
          )}
          {err && <div className="text-sm text-hh-danger">{err}</div>}
        </div>
        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onCancel} className="hh-btn-ghost">Cancel</button>
          <button onClick={() => onSave({ name, email, bio, avatar_url: avatar || null, royalty_pct: pct })}
                  className="hh-btn-primary" data-testid="author-save">
            {initial ? "Save changes" : "Create author"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, optional }) {
  return (
    <div>
      <label className="block text-sm text-hh-ink2 mb-2">
        {label} {optional && <span className="text-xs">(optional)</span>}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40" />
    </div>
  );
}
